package com.dignicate.web.ui.components.header

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.dignicate.web.domain.Language
import com.dignicate.web.theme.LocalSettings
import com.dignicate.web.theme.LocalUpdateSettings
import com.dignicate.web.theme.ThemeMode
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.marginLeft
import org.jetbrains.compose.web.css.marginTop
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent

@Composable
fun SettingsDialog(isOpen: Boolean, onClose: () -> Unit) {
    // render while open or during closing animation
    var isClosing by remember { mutableStateOf(false) }
    if (!isOpen && !isClosing) return

    // when dialog becomes open, ensure not in closing state
    LaunchedEffect(isOpen) {
        if (isOpen) isClosing = false
    }

    // helper to start closing animation and call onClose after delay
    fun startClose() {
        if (isClosing) return
        isClosing = true
        // call onClose after animation duration (180ms)
        window.setTimeout({
            try {
                onClose()
            } finally {
                isClosing = false
            }
        }, 180)
    }

    // register Esc key to close while dialog is open
    DisposableEffect(isOpen, isClosing) {
        val handler: (Event) -> Unit = { ev ->
            val ke = ev as? KeyboardEvent
            if (ke?.key == "Escape") {
                ev.preventDefault()
                startClose()
            }
        }
        window.addEventListener("keydown", handler)
        onDispose { window.removeEventListener("keydown", handler) }
    }

    val settings = LocalSettings.current
    val update = LocalUpdateSettings.current

    // overlay that covers the screen; click outside modal closes it
    val overlayStateClass = when {
        isOpen && !isClosing -> "is-open"
        isClosing -> "is-closing"
        else -> ""
    }
    Div({ classes("settings-overlay", overlayStateClass); onClick { ev -> ev.stopPropagation(); startClose() } }) {
        // modal box - stop propagation on inner click so overlay doesn't close
        val dialogStateClass = when {
            isOpen && !isClosing -> "is-open"
            isClosing -> "is-closing"
            else -> ""
        }
        Div({ classes("settings-dialog", dialogStateClass); onClick { ev -> ev.stopPropagation() } }) {
            Div({ classes("settings-header") }) {
                Div({ classes("settings-title") }) { H3 { Text("Settings") } }
                Button(attrs = { classes("settings-close", "dialog-close-button"); onClick { startClose() }; attr("aria-label", "Close Settings") }) { Text("×") }
            }

            Div({ classes("settings-section") }) {
                Div({ classes("settings-label") }) { Text("Language") }
                Select(attrs = {
                    classes("settings-select")
                    onInput { ev ->
                        val v = ev.target.value
                        val lang = when (v) {
                            "Ja" -> Language.Ja
                            "En" -> Language.En
                            else -> Language.System
                        }
                        // determine fontScale for the selected language so we update language+scale together
                        val fontScale = when (lang) {
                            Language.En -> 1.12f
                            else -> 1.0f
                        }
                        val lineHeightScale = when (lang) {
                            Language.En -> 1.8f
                            else -> 1.45f
                        }
                        update(settings.copy(language = lang, fontScale = fontScale, lineHeightScale = lineHeightScale))
                    }
                }) {
                    Option("System", attrs = { if (settings.language == Language.System) selected() }) { Text("System") }
                    Option("Ja", attrs = { if (settings.language == Language.Ja) selected() }) { Text("日本語") }
                    Option("En", attrs = { if (settings.language == Language.En) selected() }) { Text("English") }
                }
            }

            Div({ classes("settings-section") }) {
                Div({ classes("settings-label") }) { Text("Theme") }
                Select(attrs = {
                    classes("settings-select")
                    onInput { ev ->
                        val v = ev.target.value
                        val mode = when (v) {
                            "Light" -> ThemeMode.Light
                            "Dark" -> ThemeMode.Dark
                            else -> ThemeMode.System
                        }
                        update(settings.copy(theme = mode))
                    }
                }) {
                    Option("System", attrs = { if (settings.theme == ThemeMode.System) selected() }) { Text("System") }
                    Option("Light", attrs = { if (settings.theme == ThemeMode.Light) selected() }) { Text("Light") }
                    Option("Dark", attrs = { if (settings.theme == ThemeMode.Dark) selected() }) { Text("Dark") }
                }
            }

            // Animated Header section: Toggle + Speed/Intensity sliders
            // Hidden for now — keep in DOM but not visible so users don't see it until we re-enable
            Div({ classes("settings-section"); style { display(DisplayStyle.None) } }) {
                Div({ classes("settings-label") }) { Text("Animated Header") }

                // Toggle (checkbox)
                Div({ classes("settings-control") }) {
                    Input(InputType.Checkbox) {
                        if (settings.headerAnimationEnabled) attr("checked", "checked")
                        onInput { ev ->
                            val checked = ev.target.checked
                            update(settings.copy(headerAnimationEnabled = checked))
                        }
                    }
                    Div({ style { display(DisplayStyle.InlineBlock); marginLeft(8.px) } }) { Text("Enable animated header") }
                }

                // Speed slider (0..100 mapped to 0.0..1.0)
                Div({ classes("settings-control"); style { marginTop(8.px) } }) {
                    Div({ classes("settings-sub-label") }) { Text("Animation Speed") }
                    Input(InputType.Range) {
                        attr("min", "0")
                        attr("max", "100")
                        attr("value", ((settings.headerAnimationSpeed * 100).toInt()).toString())
                        onInput { ev ->
                            val v = ev.target.value.toIntOrNull() ?: 0
                            val f = (v.toFloat() / 100f).coerceIn(0f, 1f)
                            update(settings.copy(headerAnimationSpeed = f))
                        }
                    }
                }

                // Intensity slider (0..100 mapped to 0.0..1.0)
                Div({ classes("settings-control"); style { marginTop(8.px) } }) {
                    Div({ classes("settings-sub-label") }) { Text("Animation Intensity") }
                    Input(InputType.Range) {
                        attr("min", "0")
                        attr("max", "100")
                        attr("value", ((settings.headerAnimationIntensity * 100).toInt()).toString())
                        onInput { ev ->
                            val v = ev.target.value.toIntOrNull() ?: 0
                            val f = (v.toFloat() / 100f).coerceIn(0f, 1f)
                            update(settings.copy(headerAnimationIntensity = f))
                        }
                    }
                }
            }

            // No bottom Close button by design; use × or Esc to close
        }
    }
}
